/**
 * Portal Redirect Utility
 * Handles cross-portal authentication and role-based redirects
 */

export interface PortalURLs {
  plant: string;
  hub: string;
  admin: string;
}

/**
 * Get portal URLs based on current environment
 */
export const getPortalURLs = (): PortalURLs => {
  const hostname = window.location.hostname;
  
  // Production URLs
  if (hostname.includes('242network.com')) {
    return {
      plant: 'https://plant.242network.com',
      hub: 'https://hub.242network.com',
      admin: 'https://admin.242network.com',
    };
  }
  
  // Development URLs (localhost with different ports)
  return {
    plant: 'http://localhost:3001',
    hub: 'http://localhost:3002',
    admin: 'http://localhost:3003',
  };
};

/**
 * Determine which portal a user should access based on their roles
 * and the portal they're trying to access
 */
export type CurrentPortal = 'admin' | 'hub' | 'plant';

export interface RedirectDecision {
  shouldRedirect: boolean;
  targetPortal?: CurrentPortal;
  showPrompt?: boolean;
  reason?: string;
}

/**
 * Roles that mean a user has legitimate hub/staff business, so holding
 * church_planter alongside one of these should NOT force them to the
 * plant portal. Mirrors backend HUB_STAFF_PLANTER_ROLES
 * (backend/middleware/auth.js).
 */
const HUB_STAFF_ROLES = ['hub_leader', 'admin', 'staff', 'coach', 'assessor', 'trainer'];

/**
 * Get redirect decision based on user roles and current portal
 * 
 * Rules:
 * 1. Church planters with no other hub/staff role always go to plant portal
 * 2. Users with both admin AND hub_leader can access either
 * 3. Hub leaders trying to access admin → redirect to hub
 * 4. Admin only users trying to access hub → show prompt
 */
export const getRedirectDecision = (
  userRoles: string[],
  currentPortal: CurrentPortal
): RedirectDecision => {
  const hasChurchPlanter = userRoles.includes('church_planter');
  const hasAdmin = userRoles.includes('admin');
  const hasHubLeader = userRoles.includes('hub_leader');
  const hasHubStaffRole = HUB_STAFF_ROLES.some((role) => userRoles.includes(role));
  
  // Rule 1: Church planters with no other hub/staff role ALWAYS go to plant portal.
  // A dual-role user (e.g. church_planter + hub_leader) is not forced to plant;
  // they fall through to the rules below instead.
  if (hasChurchPlanter && !hasHubStaffRole) {
    if (currentPortal !== 'plant') {
      return {
        shouldRedirect: true,
        targetPortal: 'plant',
        reason: 'Church planters must use the plant portal',
      };
    }
    return { shouldRedirect: false };
  }
  
  // Rule 2: Users with both admin AND hub_leader can access either
  if (hasAdmin && hasHubLeader) {
    return { shouldRedirect: false };
  }
  
  // Rule 3: Hub leaders trying to access admin → redirect to hub
  if (hasHubLeader && !hasAdmin && currentPortal === 'admin') {
    return {
      shouldRedirect: true,
      targetPortal: 'hub',
      reason: 'Hub leaders should use the hub portal',
    };
  }
  
  // Rule 4: Admin only users trying to access hub → show prompt
  if (hasAdmin && !hasHubLeader && currentPortal === 'hub') {
    return {
      shouldRedirect: false,
      showPrompt: true,
      reason: 'Admin users typically use the admin portal',
    };
  }
  
  // Default: no redirect needed
  return { shouldRedirect: false };
};

/**
 * Perform cross-portal redirect with authentication
 * Transfers the user's token and data to the target portal
 */
export const redirectToPortal = (
  targetPortal: CurrentPortal,
  token: string,
  userData: any
): void => {
  const portalURLs = getPortalURLs();
  const targetURL = portalURLs[targetPortal];
  
  // Store auth data in localStorage (it will be available in the new domain in dev,
  // but for production we'll pass it via URL params and have the target portal store it)
  const authData = {
    token,
    user: userData,
    redirectedFrom: window.location.hostname,
    timestamp: Date.now(),
  };
  
  // For development (same domain), we can use localStorage
  // For production (different subdomains), we need to pass via URL
  if (window.location.hostname === 'localhost') {
    // Development: localStorage works across ports
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    window.location.href = targetURL;
  } else {
    // Production: Pass auth data via URL parameters (will be cleaned up by target)
    const params = new URLSearchParams({
      authRedirect: 'true',
      token: token,
      userData: JSON.stringify(userData),
    });
    window.location.href = `${targetURL}/auth-redirect?${params.toString()}`;
  }
};

/**
 * Check if we're receiving an auth redirect and handle it
 * Should be called on app initialization
 */
export const handleAuthRedirect = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  
  if (params.get('authRedirect') === 'true') {
    const token = params.get('token');
    const userDataStr = params.get('userData');
    
    if (token && userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return true;
      } catch (error) {
        console.error('Error handling auth redirect:', error);
      }
    }
  }
  
  return false;
};

